import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BUSINESS, waLink, SERVICES } from "@/data/site";

const budgets = ["< ₹25,000", "₹25,000 – ₹75,000", "₹75,000 – ₹2,00,000", "₹2,00,000+"];

export const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", business: "", email: "", phone: "", service: "", budget: "", message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can reply.");
      return;
    }
    setLoading(true);
    const text = [
      `New enquiry for ${BUSINESS.name}`,
      `Name: ${form.name}`,
      form.business && `Business: ${form.business}`,
      `Email: ${form.email}`,
      form.phone && `Phone: ${form.phone}`,
      form.service && `Service: ${form.service}`,
      form.budget && `Budget: ${form.budget}`,
      form.message && `Message: ${form.message}`,
    ].filter(Boolean).join("\n");

    setTimeout(() => {
      window.open(waLink(text), "_blank", "noopener,noreferrer");
      setLoading(false);
      toast.success("Opening WhatsApp — we'll reply within 24 hours!");
      setForm({ name: "", business: "", email: "", phone: "", service: "", budget: "", message: "" });
    }, 500);
  };

  const fieldCls =
    "bg-white border-border h-12 rounded-xl focus-visible:ring-primary focus-visible:ring-offset-0";

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className="grid gap-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="grid gap-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Your name" className={fieldCls} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="business">Business Name</Label>
          <Input id="business" data-testid="contact-business" value={form.business} onChange={update("business")} placeholder="Company / brand" className={fieldCls} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="grid gap-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={fieldCls} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="+91 ..." className={fieldCls} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="grid gap-2">
          <Label htmlFor="service">Service Required</Label>
          <select id="service" data-testid="contact-service" value={form.service} onChange={update("service")}
            className={`${fieldCls} px-3 text-sm w-full border appearance-none`}>
            <option value="">Select a service</option>
            {SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="budget">Budget</Label>
          <select id="budget" data-testid="contact-budget" value={form.budget} onChange={update("budget")}
            className={`${fieldCls} px-3 text-sm w-full border appearance-none`}>
            <option value="">Select a budget</option>
            {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="message">Message</Label>
        <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} placeholder="Tell us about your project..." rows={4}
          className="bg-white border-border rounded-xl focus-visible:ring-primary focus-visible:ring-offset-0" />
      </div>
      <motion.button
        type="submit"
        data-testid="contact-submit-btn"
        disabled={loading}
        whileTap={{ scale: 0.97 }}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-[#c81f1f] text-primary-foreground font-semibold py-4 px-6 transition-colors disabled:opacity-70"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
        {loading ? "Opening WhatsApp..." : "Send Enquiry via WhatsApp"}
      </motion.button>
      <p className="text-xs text-muted-foreground text-center">
        Your details are kept private. Submitting opens WhatsApp with your enquiry pre-filled.
      </p>
    </form>
  );
};
